'use client';

import { useSearchParams } from 'next/navigation';
import { isServer } from '@frameio/utils';

const SIGNUP_QUERY_PARAM = 'signup';

const EMPTY_URL_SEARCH_PARAMS = new URLSearchParams();

/**
 * Reads the one-shot `?signup=true` flag that auth-portal sets on the first
 * load after a brand-new signup. When true, account reads should hit the
 * primary repo (not CRDB follower reads) so the just-created account is visible.
 *
 * Returns `true` only when the flag is explicitly set, otherwise `undefined` so
 * the GraphQL `signup` variable is omitted on normal loads — that keeps the
 * Apollo cache key for everyday traffic identical to a query with no signup arg
 * (the backend treats absent and `false` the same: follower reads).
 *
 * `useSearchParams()` throws on the server and forces a static-prerender (CSR)
 * bailout, which breaks `next build` for the statically-exported pages that
 * mount this through the always-on `DefaultAccountId` provider — so we borrow
 * the `isServer` guard from `AppUrlParamsProvider` and treat params as empty on
 * the server.
 *
 * Unlike that provider, we deliberately do NOT wrap this in `useDeferredValue`.
 * The flag is a one-shot value that never changes within a session, and we need
 * it on the first render so the initial account read already carries `signup`
 * and hits the primary repo. Deferring it would let that first read fire as a
 * follower read — the exact stale-read this exists to avoid.
 */
export const useSignupFromQueryParam = (): boolean | undefined => {
  const searchParams = isServer
    ? EMPTY_URL_SEARCH_PARAMS
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSearchParams();
  return searchParams.get(SIGNUP_QUERY_PARAM) === 'true' ? true : undefined;
};
